import { FC, useEffect, useRef } from 'react'
import { json, Outlet, replace, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './auth.css'

// COMPONENTS
import { Column } from '@components/templates/column'
import Toast from '@components/molecules/toast'

// ROUTES
import SignInRoute from './sign-in'
import VerifyRoute from './verify'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { AuthContext } from './_localStore/reducer'
import { isNonEmptyString } from '@utils/type-checkers'
import { parseTeraUserData } from '@schemas'
import { createLoader } from '@utils/loaders'
import { useAuthReducer } from './_localStore/_hooks'
import * as Sentry from '@sentry/react'
import { VirtualCardAPI } from '@apis'


interface Props { }

export type AuthScreen = typeof authScreens[number]
export const authScreens = ["sign-in", "verify"] as const

function normalizeReferralId(referralId: string | null): string {
    if (!isNonEmptyString(referralId)) return ''
    return referralId.trim().replace(/^\{/, '').replace(/\}$/, '')
}

const Layout: FC<Props> = () => {
    const authReducer = useAuthReducer()
    const { isLoaded, isSignedIn, user } = useUser()
    const { toast } = authReducer.localState
    const handledReferralIdRef = useRef<string | null>(null)

    useEffect(() => {
        // The user shouldn't be able to access the Auth screens if they are already signed in when Clerk loads.
        if (isLoaded && isSignedIn) { } //navigation.navigate(RootScreens.Account)

        // `isSignedIn` is omitted since it can become true before the user has finished the sign-up process.
        // This effect should only be ran when Clerk is initially loaded (and when the auth screens are focused).
        // eslint-disable-next-line
    }, [isLoaded])

    const navigate = useNavigate()
    useEffect(() => {
        Sentry.addBreadcrumb({ category: 'auth', message: 'layout useEffect triggered', level: 'info', data: { isLoaded, isSignedIn, firstName: user?.firstName, lastName: user?.lastName } })
        if (!isLoaded || !isSignedIn) return

        async function handleSignedInRedirect() {
            Sentry.addBreadcrumb({ category: 'auth', message: 'user is signed in, determining redirect', level: 'info' })

            const searchParams = new URLSearchParams(document.location.search)
            const referralId = normalizeReferralId(searchParams.get('referralId'))

            if (isNonEmptyString(referralId) && handledReferralIdRef.current !== referralId) {
                handledReferralIdRef.current = referralId
                Sentry.addBreadcrumb({ category: 'auth', message: 'referralId detected, claiming promo', level: 'info', data: { referralId } })

                try {
                    await VirtualCardAPI.ClaimPromoRequest.fetch(referralId)
                    Sentry.addBreadcrumb({ category: 'auth', message: 'promo claim successful', level: 'info', data: { referralId } })
                } catch (error) {
                    console.error('[ Auth ] promo claim failed', error)
                    Sentry.addBreadcrumb({ category: 'auth', message: 'promo claim failed', level: 'error', data: { referralId } })
                }

                searchParams.delete('referralId')
                const query = searchParams.toString()
                const path = `${document.location.pathname}${query ? `?${query}` : ''}${document.location.hash}`
                window.history.replaceState({}, '', path)
            }

            if (!isNonEmptyString(user?.firstName) || !isNonEmptyString(user?.lastName)) {
                Sentry.addBreadcrumb({ category: 'auth', message: 'missing name, redirecting to /sign-up/add-info', level: 'info' })
                const params = new URLSearchParams(document.location.search)
                params.delete('referralId')
                navigate(`/sign-up/add-info${params.toString() ? `?${params.toString()}` : ''}`);
                return
            }

            // new user logic
            const tera = parseTeraUserData(window.Clerk?.user?.publicMetadata.tera)
            const landingPage = !tera.address ? '/sign-up/billing' : '/account'
            Sentry.addBreadcrumb({ category: 'auth', message: 'determined landingPage', level: 'info', data: { landingPage, teraAddress: tera.address } })

            const from = new URLSearchParams(document.location.search).get('from')
            Sentry.addBreadcrumb({ category: 'auth', message: 'from parameter', level: 'info', data: { from, locationSearch: document.location.search } })

            // Sanitize the 'from' parameter - strip any protocol prefix (like capacitor://)
            // that may have been incorrectly stored. Clerk doesn't recognize capacitor:// 
            // as a valid protocol and would otherwise redirect to "/" causing a login loop.
            let redirect = landingPage
            if (isNonEmptyString(from)) {
                // Remove protocol and host if present (e.g., "capacitor://localhost/path" -> "/path")
                const cleanPath = from.replace(/^[a-z]+:\/\/[^/]*/, '')
                redirect = cleanPath.startsWith('/') ? cleanPath : landingPage
                Sentry.addBreadcrumb({ category: 'auth', message: 'sanitized from parameter', level: 'info', data: { from, cleanPath, redirect } })
            }

            Sentry.addBreadcrumb({ category: 'auth', message: 'navigating to redirect', level: 'info', data: { redirect } })
            navigate(redirect, { replace: true })
        }

        handleSignedInRedirect()
    }, [isLoaded, isSignedIn, user?.id, user?.firstName, user?.lastName, navigate])

    if (!isLoaded) return null
    if (isSignedIn) return null

    return (
        <AuthContext.Provider value={authReducer}>
            <Column className="auth-stack">
                <Outlet />
                <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'var(--header-height)', zIndex: 100, pointerEvents: 'none' }}>
                    <Toast toast={toast} />
                </div>
            </Column>
        </AuthContext.Provider >
    )
}

const loader = createLoader(({ request }) => {
    const url = new URL(request.url)
    if (url.pathname === '/' || url.pathname === '/auth' || url.pathname === '/auth/') return replace(`/sign-in?${url.searchParams.toString()}`)
    else return json(null, 200)
})

const AuthRoute: RouteObject = {
    path: "/",
    element: <Layout />,
    errorElement: null,
    loader,
    children: [
        SignInRoute,
        VerifyRoute,
    ]
}

export default AuthRoute
